import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm, useWatch } from 'react-hook-form'

import { getApiErrorMessage } from '#/shared/api/api-error'
import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import { Input } from '#/shared/ui/input'

import { AUTH_CARD_CLASS, AuthCardHeader } from './auth-screen'
import { apiErrorCode, useRecoverySignIn } from './passkeys'
import { recoverySchema, type RecoveryValues } from './schema'

function recoveryError(error: unknown): string {
  if (apiErrorCode(error) === 'RECOVERY_CODE_INVALID') {
    return 'That code does not work for this account — it may be mistyped or already used.'
  }
  return getApiErrorMessage(error)
}

/**
 * The way back in after losing every passkey.
 *
 * A code is spent the moment it is accepted, and it does not open the console
 * by itself: the next screen adds a new passkey first, so the account is never
 * left with nothing to sign in with.
 */
export function RecoveryForm() {
  const navigate = useNavigate()
  const recover = useRecoverySignIn()

  const {
    control,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RecoveryValues>({
    resolver: zodResolver(recoverySchema),
    defaultValues: { email: '', code: '', remember: false },
  })
  const remember = useWatch({ control, name: 'remember' })

  const onSubmit = handleSubmit((values) => {
    recover.mutate(values, { onSuccess: () => void navigate({ to: '/login/passkey' }) })
  })

  return (
    <form onSubmit={(event) => void onSubmit(event)} noValidate className={AUTH_CARD_CLASS}>
      <AuthCardHeader
        title="Use a recovery code"
        subtitle="Each code works once. You add a new passkey right after."
      />
      <div className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="you@thecargo.io"
          error={Boolean(errors.email)}
          {...register('email')}
        />
        {errors.email ? (
          <p role="alert" className="text-label text-red-500">
            {errors.email.message}
          </p>
        ) : null}
        <Input
          label="Recovery code"
          autoComplete="one-time-code"
          spellCheck={false}
          placeholder="XXXXX-XXXXX"
          autoCapitalize="characters"
          error={Boolean(errors.code)}
          {...register('code')}
        />
        {errors.code ? (
          <p role="alert" className="text-label text-red-500">
            {errors.code.message}
          </p>
        ) : null}
        <label className="flex items-center gap-2 text-sm text-gray-900">
          <Checkbox
            checked={remember}
            onCheckedChange={(next) => setValue('remember', next === true)}
          />
          Keep me signed in
        </label>
      </div>
      {recover.isError ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-500">
          {recoveryError(recover.error)}
        </p>
      ) : null}
      <Button type="submit" size="lg" loading={recover.isPending} className="w-full">
        Continue
      </Button>
      <Link to="/login" className="text-label -mt-2 text-center text-gray-500 hover:text-gray-900">
        Back to sign in
      </Link>
    </form>
  )
}
